import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip, type StatusChipColor } from "components/StatusChip";

export interface DiscrepancyCounts {
  unresolved: number;
  pending: number;
  resolved: number;
  /** Color for the unresolved slot; pending color; resolved is always green. */
  unresolvedColor: StatusChipColor;
  pendingColor: StatusChipColor;
}

/** Build the three slots (unresolved, pending, resolved) in fixed display order. */
function discrepancySlots({
  unresolved,
  pending,
  resolved,
  unresolvedColor,
  pendingColor,
}: DiscrepancyCounts) {
  return [
    { key: "unresolved", label: "pending review", count: unresolved, color: unresolvedColor },
    {
      key: "pending",
      label: "needs correction in e-Manifest",
      count: pending,
      color: pendingColor,
    },
    { key: "resolved", label: "resolved", count: resolved, color: "success" as StatusChipColor },
  ];
}

/** Tooltip text summarizing all three slots ("3 pending review · 2 needs correction in e-Manifest · 0 resolved"). */
function discrepancyTooltip(counts: DiscrepancyCounts): string {
  return discrepancySlots(counts)
    .map((slot) => `${slot.count} ${slot.label}`)
    .join(" · ");
}

/**
 * Three-slot discrepancy bubble (pending review / needs correction in e-Manifest / resolved). When
 * the entity has any discrepancy, all three slots take space so columns line up —
 * a zero slot shows the neutral "—" placeholder instead of a bubble. An entity
 * with no discrepancies at all renders nothing.
 *
 * Shared by the staff manifest-review grid and the customer manifest / line-item
 * grids so the Encamp-flagged discrepancy breakdown renders identically on both.
 */
export function DiscrepancyBubblesCell(props: DiscrepancyCounts & { onClick?: () => void }) {
  if (props.unresolved === 0 && props.pending === 0 && props.resolved === 0) return null;
  const { onClick } = props;
  return (
    <Tooltip title={discrepancyTooltip(props)} placement="top" enterDelay={400}>
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        onClick={
          onClick
            ? (event) => {
                event.stopPropagation();
                onClick();
              }
            : undefined
        }
        sx={{ cursor: onClick ? "pointer" : "default" }}
      >
        {discrepancySlots(props).map((slot) => (
          <Box key={slot.key} sx={{ minWidth: 24, display: "flex", justifyContent: "center" }}>
            {slot.count > 0 ? (
              <StatusChip label={slot.count} color={slot.color} variant="soft" />
            ) : (
              <Typography component="span" color="text.disabled" aria-hidden>
                —
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Tooltip>
  );
}
